import React, { useState, useEffect, useMemo } from 'react'
import reduce from 'lodash/reduce'
import find from 'lodash/find'
import classnames from 'classnames'
import Filters from '@Images/projects_table_filters.svg'
import Arrow from '@Images/filters_dropdown_arrow.svg'
import SelectedItems from './SelectedItems'
import Button from '@SiteComponents/Button'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'

import './FiltersDropdown.scss'

interface ICategory {
  readonly name: string
  readonly icon?: React.ReactNode
  readonly selectedItemIds: (number | string)[]
  readonly selectedItemCaptions: string[]
  readonly itemManagerFactory: () => React.ReactNode
  resetItemSelection(): void
}

interface FiltersDropdownProps {
  readonly categories: ICategory[]
  readonly disabled?: boolean
}

const FiltersDropdown: React.FC<FiltersDropdownProps> = (props) => {
  const {
    categories,
    disabled,
  } = props

  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false)
  const [openedManager, setOpenedManager] = useState<string | null>(null)

  // proxy const for useClickOutsideListener
  const shouldShowManager = openedManager !== null
  const setShouldShowManager = (value: boolean) => {
    if (value) { return }

    setOpenedManager(null)
  }

  useClickOutsideListener('FiltersDropdown', setShowFiltersDropdown, showFiltersDropdown)
  useClickOutsideListener('FiltersDropdown__filters-dropdown-items-container', setShouldShowManager, shouldShowManager)

  useEffect(()=> {
    const projectsTableListBodyElement = document.querySelector('.ProjectsTableList__body')
    if (projectsTableListBodyElement) {
      (projectsTableListBodyElement as HTMLElement).style.overflowY = showFiltersDropdown ? 'hidden' : 'scroll'
    }

    const projectsTableGridElement = document.querySelector('.ProjectsTableGrid')
    if (projectsTableGridElement) {
      (projectsTableGridElement as HTMLElement).style.overflowY = showFiltersDropdown ? 'hidden' : 'scroll'
    }
  }, [showFiltersDropdown])

  const toggleFiltersDropdown = () => {
    setShowFiltersDropdown(!showFiltersDropdown)
  }

  const itemCategories = useMemo(() => {
    return reduce(categories, (acc, category) => {
      const {
        name,
        icon,
      } = category

      const isOpened = openedManager === name

      const displayItemManager = () => {
        setOpenedManager(name)
      }

      acc.push(
        <li
          key={name}
          className={classnames(
            'FiltersDropdown__filters-dropdown-item',
            { 'FiltersDropdown__filters-dropdown-item-active': isOpened }
          )}
          onMouseEnter={displayItemManager}
        >
          <div className="FiltersDropdown__filters-dropdown-item-left">
            {icon}
            {name}
          </div>
          <div className="FiltersDropdown__filters-dropdown-item-right">
            <Arrow />
          </div>
        </li>
      )

      return acc
    }, [] as React.ReactNode[])
  }, [categories, openedManager])

  const renderManager = () => {
    const activeCategory = find(categories, category => category.name === openedManager)

    return activeCategory ? activeCategory.itemManagerFactory() : null
  }

  const renderFiltersDropdown = () => {
    return (
      <div className="FiltersDropdown__filters-dropdown">
        <div  className="FiltersDropdown__filters-dropdown-items-container">
          <ul className="FiltersDropdown__filters-dropdown-items">
            {itemCategories}
          </ul>
          {shouldShowManager && renderManager()}
        </div>
      </div>
    )
  }

  const selectedItems = useMemo(() => {
    return reduce(categories, (acc, category) => {
      const {
        name,
        icon,
        selectedItemIds,
        selectedItemCaptions,
        resetItemSelection,
      } = category

      if (selectedItemIds.length > 0) {
        acc.push(
          <SelectedItems
            key={name}
            categoryName={name}
            categoryIcon={icon}
            selectedItemIds={selectedItemIds}
            selectedItemCaptions={selectedItemCaptions}
            resetItemSelection={resetItemSelection}
            includeType="any"
          />
        )
      }
      return acc
    }, [] as React.ReactNode[])
  }, [categories])

  return (
    <div className="FiltersDropdown">
      <Button
        caption="Filters"
        icon={<Filters />}
        styleType={showFiltersDropdown ? 'gray-subtle-active' : 'gray-subtle'}
        size="large"
        iconRight
        onClick={toggleFiltersDropdown}
        disabled={disabled}
      />
      {showFiltersDropdown && renderFiltersDropdown()}
      {selectedItems}
    </div>
  )
}

export default FiltersDropdown
